import {expectEither, expectOneOf, POST, useHttpClient} from '@raison/network-facade';
import {useMutation, UseMutationOptions, useSuspenseQuery, UseSuspenseQueryOptions} from '@tanstack/react-query';
import {formatDate} from 'date-fns';
import {useAtomValue} from 'jotai';
import {Logout$} from '~/entities/user';
import {SessionExpiredError} from '~/entities/user/model/SessionExpiredError';
import {TransactionsForm} from '../ui/TransactionsReportForm';
import {SuccessResponseSchema} from './schemas/successResponseSchema';
import {TransactionsSchema} from './schemas/transactionsSchema';

export function useTransactionsQuery(
    options?: Omit<UseSuspenseQueryOptions<TransactionsSchema>, 'queryKey' | 'queryFn'>
) {
    const http = useHttpClient();

    return useSuspenseQuery({
        ...options,
        queryKey: ['/v1/reports/transactions'],
        queryFn: async () => {
            const response = await http('/v1/reports/transactions').then(
                ...expectEither({
                    200: TransactionsSchema,
                })
            );

            if (response.status === 'Abort') {
                throw response.reason;
            }

            return response.json;
        },
    });
}

export function useTransactionsMutation(
    options?: Omit<UseMutationOptions<SuccessResponseSchema, unknown, TransactionsForm>, 'mutationKey' | 'mutationFn'>
) {
    const http = useHttpClient();
    const logout = useAtomValue(Logout$);

    return useMutation<SuccessResponseSchema, unknown, TransactionsForm>({
        ...options,
        mutationKey: ['/v1/reports/transactions'],
        mutationFn: async ({startDate, endDate, service, transaction, format, asset, dateRange}) => {
            const url = `/v1/reports/transactions/${service}/${transaction}/${asset}`;
            const params = new URLSearchParams({
                format: format,
            });
            if (dateRange === 'custom-range') {
                if (startDate) {
                    params.set('from', formatDate(startDate, 'yyyy-MM-dd'));
                }
                if (endDate) {
                    params.set('to', formatDate(endDate, 'yyyy-MM-dd'));
                }
            }
            const response = await http(`${url}?${params}`, POST()).then(
                expectOneOf({200: SuccessResponseSchema, 401: null})
            );

            if (response.status === 401) {
                logout();
                throw new SessionExpiredError();
            }

            return response.json;
        },
    });
}
